/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatTableDataSource} from '@angular/material/table';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../common/constants';
import {AddPopUpComponent} from '../../../core/components/pop-ups/add-pop-up/add-pop-up.component';
import {MarketingDivisionService} from '../../../services/marketing-division.service';

@Component({
    selector: 'app-manage-marketing-divisions',
    templateUrl: './manage-marketing-divisions.component.html',
    styleUrls: ['./manage-marketing-divisions.component.scss'],
    standalone: false
})
export class ManageMarketingDivisionsComponent implements OnInit {
  showSpinner: boolean;
  noData: boolean;
  displayedColumns: string[] = ['marketDivision'];
  dataSource: MatTableDataSource<any> = new MatTableDataSource(null);
  marketDivisions: any[] = [];

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  columnDef: ColDef[] = [
    {field: 'mktDiv', headerName: 'Market Division'}];

  constructor(public snackBar: FabMessageService, private readonly marketDivisionService: MarketingDivisionService,
              public dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.loadMarketDivisions();
  }

  /**
   * fetches data from backend, loads it into marketDivisions, and displays it in the table
   * if list is empty, hides table and displays message telling the user the table is empty
   * @return void
   */
  loadMarketDivisions(): void {
    this.showSpinner = true;
    this.marketDivisionService.getCurrentMarketDivisions().subscribe(
      data => {
        this.marketDivisions = [];
        for (const marketDivision of data) {
          this.marketDivisions.push({mktDiv: marketDivision});
        }
      },
      (err) => {
        this.showSpinner = false;
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      () => {
        this.showSpinner = false;
        this.dataSource = new MatTableDataSource(this.marketDivisions);
        this.noData = (this.marketDivisions.length === 0);
      }
    );
  }

  /**
   * Adds a new Market Division to DB.
   */
  addMarketDivision(): void {
    const dialogRef = this.dialog.open(AddPopUpComponent, {
      data: {
        title: 'Add',
        maxLength: 15
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.marketDivisionService.addMarketDivision(res.data.newValue).subscribe({
          error: err => {
            this.errorHandle(err);
            this.loadMarketDivisions();
          },
          complete: () => {
            this.snackBar.displaySuccess('Successfully added market division.', DISMISS_MSG);
            this.loadMarketDivisions();
          }
        });
      }
    });
  }

  /**
   * display message on error.
   * @param error custom error message.
   */
  errorHandle(error: string): void {
    this.showSpinner = false;
    this.snackBar.displayError(error, DISMISS_MSG);
  }
}
